import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Award, HeartHandshake, FlaskConical, ArrowRight, Beaker, Syringe, Leaf, Pill, ShieldCheck, Sparkles, Phone } from "lucide-react";
import { FEATURED } from "../data/products";
import { Reveal } from "../components/Reveal";
import { SITE, waLink } from "../lib/site";

const SLIDES = [
  {
    img: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=1800&q=70",
    eyebrow: "Dairy Productivity",
    title: "Healthy Cattle, Maximum Yield",
    subtitle: "Premium veterinary medicines trusted by dairy farmers across India.",
    cta1: { label: "Explore Products", to: "/products" },
    cta2: { label: "Contact Us", to: "/contact" },
  },
  {
    img: "https://images.unsplash.com/photo-1507103011901-e954d6ec0988?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    eyebrow: "Calcitonic-DS",
    title: "Stronger Bones. Higher Milk. Healthier Animals.",
    subtitle: "Chelated Calcitonic-DS — India's trusted calcium supplement for dairy cattle.",
    cta1: { label: "View Product", to: "/products/chelated-calcitonic-ds" },
    cta2: { label: "Get a Quote", to: "/contact" },
  },
  {
    img: "/assets/products/masta-go-1.jpeg",
    eyebrow: "Full Range",
    title: "A Fullfill Desire of Animal Health Care",
    subtitle: "Complete range of veterinary pharmaceuticals for cattle, buffalo, goat & poultry.",
    cta1: { label: "Our Products", to: "/products" },
    cta2: { label: "Contact Us", to: "/contact" },
  },
];

const CATEGORY_CARDS = [
  { icon: Beaker, name: "Calcium & Nutritional Supplements", desc: "Chelated minerals & vitamins for peak productivity.", filter: "nutritional" },
  { icon: Syringe, name: "Antibiotics & Injections", desc: "Broad-spectrum treatment for bacterial infections.", filter: "antibiotics" },
  { icon: Leaf, name: "Liver Tonics", desc: "Hepatoprotective solutions for better metabolism.", filter: "liver" },
  { icon: Pill, name: "Antiparasitics & Dewormers", desc: "Complete endo & ecto parasite control.", filter: "antiparasitics" },
  { icon: ShieldCheck, name: "Mastitis & Udder Care", desc: "Pre & post treatment solutions for udder health.", filter: "mastitis" },
  { icon: Sparkles, name: "Uterine Care", desc: "Post-partum recovery & reproductive health.", filter: "uterine" },
];

const USPS = [
  { icon: Award, title: "Quality Assured", desc: "GMP certified veterinary formulations." },
  { icon: HeartHandshake, title: "Farmer First", desc: "Trusted by thousands of dairy farmers nationwide." },
  { icon: FlaskConical, title: "Expert Formulas", desc: "Science-backed solutions for livestock health." },
];

const STATS = [
  { value: 15, suffix: "+", label: "Products for Animal Health" },
  { value: 4000, suffix: "+", label: "Farmers & Vets Served" },
  // { value: 28, suffix: "", label: "States — Pan India" },
  { value: 100, suffix: "%", label: "GMP Certified Manufacturing" },
];

const Counter = ({ to, suffix }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start;
    const duration = 1600;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(progress * to));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return <span>{val.toLocaleString()}{suffix}</span>;
};

const HeroSlider = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);
  const go = (n) => setIdx((idx + n + SLIDES.length) % SLIDES.length);

  return (
    <section className="relative h-[88vh] min-h-[560px] overflow-hidden bg-brand-ink hero-grain" data-testid="hero-slider">
      {SLIDES.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}
          aria-hidden={i !== idx}
        >
          <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/85 via-brand-ink/55 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 w-full">
              <div className="max-w-2xl text-white">
                <div className="inline-block px-3 py-1 rounded-md bg-brand-orange/15 border border-brand-orange/40 text-brand-orange font-heading font-bold text-xs tracking-[0.2em] uppercase mb-5">{s.eyebrow}</div>
                <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 tracking-tight">{s.title}</h1>
                <p className="text-base sm:text-lg text-white/85 mb-8 max-w-xl leading-relaxed">{s.subtitle}</p>
                <div className="flex flex-wrap gap-3">
                  <Link to={s.cta1.to} data-testid={`hero-cta-primary-${i}`} className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-heading font-semibold text-sm px-6 py-3.5 rounded-md transition-colors">
                    {s.cta1.label} <ArrowRight size={16} />
                  </Link>
                  <Link to={s.cta2.to} data-testid={`hero-cta-secondary-${i}`} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/30 font-heading font-semibold text-sm px-6 py-3.5 rounded-md transition-colors">
                    {s.cta2.label}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Controls */}
      <button onClick={() => go(-1)} data-testid="hero-prev" aria-label="Previous slide" className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-md bg-white/10 hover:bg-brand-orange backdrop-blur border border-white/20 text-white flex items-center justify-center transition-colors">
        <ChevronLeft size={20} />
      </button>
      <button onClick={() => go(1)} data-testid="hero-next" aria-label="Next slide" className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-md bg-white/10 hover:bg-brand-orange backdrop-blur border border-white/20 text-white flex items-center justify-center transition-colors">
        <ChevronRight size={20} />
      </button>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            data-testid={`hero-dot-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-brand-orange" : "w-5 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>
    </section>
  );
};

const ProductCard = ({ p }) => (
  <Link to={`/products/${p.slug}`} data-testid={`featured-card-${p.slug}`} className="product-card group block bg-white border border-brand-line rounded-lg overflow-hidden">
    <div className="aspect-[4/3] bg-brand-surface overflow-hidden">
      <img src={p.images[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
    </div>
    <div className="p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-heading font-bold tracking-[0.18em] uppercase text-brand-orange">{p.categoryLabel}</span>
        <span className="text-[10px] font-heading font-bold tracking-wider uppercase px-2 py-0.5 rounded bg-brand-green/10 text-brand-green">In Stock</span>
      </div>
      <h3 className="font-heading font-bold text-base uppercase tracking-wide text-brand-ink mb-1.5 leading-snug">{p.name}</h3>
      <p className="text-sm text-brand-ink/65 line-clamp-2 mb-4">{p.tagline}</p>
      <div className="inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-brand-orange">
        Inquiry <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
      </div>
    </div>
  </Link>
);

export default function Home() {
  return (
    <div data-testid="page-home">
      <HeroSlider />

      {/* Trust Badges */}
      <section className="bg-white border-b border-brand-line">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {USPS.map((u, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-md bg-brand-orange/10 text-brand-orange flex items-center justify-center shrink-0">
                  <u.icon size={22} />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-base uppercase tracking-wide text-brand-ink mb-1">{u.title}</h4>
                  <p className="text-sm text-brand-ink/70">{u.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="bg-brand-surface py-20" id="categories">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="max-w-2xl mb-12">
              <div className="text-brand-orange font-heading font-bold text-xs tracking-[0.22em] uppercase mb-3">What We Offer</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-brand-ink mb-4 tracking-tight">Our Product Categories</h2>
              <p className="text-base text-brand-ink/65">Comprehensive solutions for complete animal health — from nutrition to treatment, every stage covered.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CATEGORY_CARDS.map((c, i) => (
              <Reveal key={i} delay={i * 60}>
                <Link to={`/products?cat=${c.filter}`} data-testid={`category-card-${c.filter}`} className="product-card block bg-white border border-brand-line rounded-lg p-7 h-full">
                  <div className="w-12 h-12 rounded-md bg-brand-orange/10 text-brand-orange flex items-center justify-center mb-5">
                    <c.icon size={22} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-brand-ink mb-2 leading-snug">{c.name}</h3>
                  <p className="text-sm text-brand-ink/65 mb-4 leading-relaxed">{c.desc}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-brand-orange">View Range <ArrowRight size={14} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
              <div className="max-w-xl">
                <div className="text-brand-orange font-heading font-bold text-xs tracking-[0.22em] uppercase mb-3">Featured Products</div>
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-brand-ink tracking-tight">Trusted by Vets &amp; Farmers</h2>
              </div>
              <Link to="/products" data-testid="home-view-all-products" className="inline-flex items-center gap-2 font-heading font-semibold text-sm text-brand-orange hover:gap-3 transition-all">
                View All Products <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURED.map((p, i) => (
              <Reveal key={p.slug} delay={i * 50}><ProductCard p={p} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="bg-brand-surface py-20">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div>
              <div className="text-brand-orange font-heading font-bold text-xs tracking-[0.22em] uppercase mb-3">About Us</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-brand-ink mb-5 tracking-tight">Built for Indian Livestock. Trusted by Vets.</h2>
              <p className="text-base text-brand-ink/75 leading-relaxed mb-4">
                At Desire Vet Care Pvt. Ltd., we are committed to fulfilling every desire of animal health care. Based in Karol Bagh, New Delhi, we manufacture and supply high-quality veterinary medicines for cattle, buffalo, goat, sheep and poultry across India.
              </p>
              <p className="text-base text-brand-ink/75 leading-relaxed mb-7">
                Our formulations are science-backed, farmer-trusted, and designed for the real-world challenges of Indian dairy and livestock farming.
              </p>
              <Link to="/about" data-testid="home-about-cta" className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-heading font-semibold text-sm px-6 py-3.5 rounded-md transition-colors">
                Know More <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-2 gap-4">
              <img src="/assets/products/calcitonic-ds-farm.jpeg" alt="Farm visit" className="rounded-lg object-cover h-56 w-full" />
              <img src="https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=600&q=70" alt="Holstein cattle" className="rounded-lg object-cover h-56 w-full mt-8" />
              <img src="/assets/products/calcitonic-ds-buffalo.jpeg" alt="Buffalo" className="rounded-lg object-cover h-56 w-full -mt-4" />
              <img src="/assets/products/calcitonic-ds-bottle1.jpeg" alt="Product bottle" className="rounded-lg object-cover h-56 w-full mt-4" />
            </div>
          </Reveal>
        </div>

        {/* Stats */}
        <div className="max-w-7xl mx-auto px-6 mt-16 grid grid-cols-2 md:grid-cols-3 max-[400px]:grid-cols-1 gap-6">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="bg-white border border-brand-line rounded-lg p-7 text-center" data-testid={`stat-${i}`}>
                <div className="font-heading font-black text-4xl sm:text-5xl text-brand-orange tracking-tight mb-1">
                  <Counter to={s.value} suffix={s.suffix} />
                </div>
                <div className="text-xs sm:text-sm text-brand-ink/70 font-heading font-semibold uppercase tracking-wider">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="max-w-2xl mb-12">
              <div className="text-brand-orange font-heading font-bold text-xs tracking-[0.22em] uppercase mb-3">Why Choose Us</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-brand-ink tracking-tight">The Desire Vet Care Difference</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { title: "No Withdrawal Period", desc: "Safe for dairy animals during lactation (for select products)." },
              { title: "Chelated Minerals", desc: "Superior absorption vs standard supplements — better results in less time." },
              { title: "Veterinarian Recommended", desc: "Trusted by licensed vets across 28+ Indian states." },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="bg-brand-surface border border-brand-line rounded-lg p-7 h-full">
                  <div className="w-10 h-10 rounded-md bg-brand-green/10 text-brand-green flex items-center justify-center mb-4 font-heading font-bold">0{i + 1}</div>
                  <h3 className="font-heading font-bold text-lg text-brand-ink mb-2">{f.title}</h3>
                  <p className="text-sm text-brand-ink/70 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA Banner */}
      <section className="bg-brand-orange relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />
        <div className="max-w-7xl mx-auto px-6 py-16 relative flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="text-white max-w-xl">
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl mb-3 tracking-tight">Ready to improve your livestock's health?</h2>
            <p className="text-white/90 text-base">Talk to our team for product guidance, dosage advice and bulk inquiries.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="banner-contact-cta" className="inline-flex items-center gap-2 bg-white text-brand-orange hover:bg-brand-ink hover:text-white font-heading font-bold text-sm px-7 py-4 rounded-md transition-colors">
              Contact Us Now <ArrowRight size={16} />
            </Link>
            <a href={waLink('Hi Desire Vet Care, I would like product guidance.')} target="_blank" rel="noreferrer" data-testid="banner-wa-cta" className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white font-heading font-bold text-sm px-7 py-4 rounded-md transition-colors">
              <Phone size={16} /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
